import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ClipImage } from '@/components/ClipImage';
import { Meta } from '@/components/Meta';
import { ParallaxImage } from '@/components/ParallaxImage';
import { Reveal } from '@/components/Reveal';
import { categoryLabels, getProject, projects } from '@/data/projects';
import { useLang } from '@/hooks/useLang';
import { NotFoundPage } from './NotFound';

export function WorkDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const lang = useLang();
  const project = getProject(slug);
  if (!project) return <NotFoundPage />;

  const title = lang === 'en' && project.titleEn ? project.titleEn : project.title;
  const summary = lang === 'en' && project.summaryEn ? project.summaryEn : project.summary;
  const idx = projects.findIndex((p) => p.slug === project.slug);
  const next = projects[(idx + 1) % projects.length] ?? project;
  const nextTitle = lang === 'en' && next.titleEn ? next.titleEn : next.title;

  return (
    <>
      <Meta title={`${title} — Silvana Barbato`} description={summary} />

      <header className="u-container grid gap-8 pt-[22vh] pb-14 md:grid-cols-12">
        <div className="md:col-span-8">
          <p className="eyebrow text-stone">
            {categoryLabels[project.category][lang]} · {project.location} · {project.year}
          </p>
          <h1 className="mt-5 font-display text-6xl md:text-8xl">{title}</h1>
        </div>
        <p className="self-end leading-relaxed text-ink-soft md:col-span-4">{summary}</p>
      </header>

      <div className="space-y-6 md:space-y-10">
        {project.images.map((src, i) =>
          i % 3 === 0 ? (
            <ParallaxImage
              key={src}
              src={src}
              alt={`${title} — ${i + 1}`}
              className="h-[70vh] w-full md:h-[100vh]"
            />
          ) : (
            <div key={src} className="u-container">
              <ClipImage src={src} alt={`${title} — ${i + 1}`} className="aspect-16/10 w-full" />
            </div>
          ),
        )}
      </div>

      <section className="u-container flex items-center justify-between gap-6 border-t border-line py-16">
        <Link to="/lavori" className="link-line eyebrow text-stone">
          ← {t('project.backToWork')}
        </Link>
        <Link to={`/lavori/${next.slug}`} className="group text-right">
          <p className="eyebrow text-stone">{t('project.next')}</p>
          <Reveal>
            <span className="font-display text-3xl transition-opacity group-hover:opacity-60 md:text-5xl">
              {nextTitle} →
            </span>
          </Reveal>
        </Link>
      </section>
    </>
  );
}
